import { Component, computed, input, output } from '@angular/core';
import { IconComponent, TooltipComponent } from '@purolator/ui-kit-angular';
import type { AlertToastSeverity } from './alert-toast.model';

const SEVERITY_ICON_SRC: Record<AlertToastSeverity, string> = {
  info: 'assets/icons/info-circle-tooltip-blue.svg',
  warning: 'assets/icons/alert-yellow.svg',
  error: 'assets/icons/alert-red.svg',
  success: 'assets/icons/success-validated.svg',
};

const SEVERITY_CLOSE_ICON_SRC: Record<AlertToastSeverity, string> = {
  info: 'assets/icons/close-circle-blue.svg',
  warning: 'assets/icons/close-circle-orange.svg',
  error: 'assets/icons/close-circle-red.svg',
  success: 'assets/icons/close-circle-green.svg',
};

/**
 * Generic Figma Toast banner with info, warning, error, and success variants.
 * Supports projected content or a plain message string.
 */
@Component({
  selector: 'app-alert-toast',
  imports: [IconComponent, TooltipComponent],
  templateUrl: './alert-toast.html',
  styleUrl: './alert-toast.scss',
  host: {
    class: 'alert-toast-host',
    '[class.alert-toast-host--hidden]': 'hidden()',
  },
})
export class AlertToast {
  private static nextTooltipDescId = 0;

  readonly severity = input.required<AlertToastSeverity>();
  /** Plain-text message; when omitted, content is projected via ng-content. */
  readonly message = input<string | null>(null);
  readonly dismissible = input(false);
  /** Optional info tooltip shown on the trailing control (Figma Toast info icon). */
  readonly tooltipContent = input<string | null>(null);
  readonly tooltipAriaLabel = input('More information');
  readonly closeAriaLabel = input('Close');
  readonly hidden = input(false);
  readonly live = input<'assertive' | 'polite' | 'off'>('off');

  readonly dismissed = output<void>();

  /** Stable id linking the info button to the visually hidden tooltip description. */
  protected readonly tooltipDescId = `alert-toast-tooltip-desc-${AlertToast.nextTooltipDescId++}`;

  protected readonly iconSrc = computed(() => SEVERITY_ICON_SRC[this.severity()]);
  protected readonly closeIconSrc = computed(() => SEVERITY_CLOSE_ICON_SRC[this.severity()]);

  protected readonly role = computed(() => {
    if (this.live() !== 'off') {
      return 'alert';
    }

    return this.severity() === 'info' ? 'status' : 'alert';
  });

  /** Emits when the customer dismisses a dismissible toast banner. */
  protected onDismiss(): void {
    this.dismissed.emit();
  }
}
